import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BellRing, Plus, Trash2, Pencil, X, Loader2, AlertTriangle } from "lucide-react";

const PLATFORMS = [
    { value: "", label: "All platforms" },
    { value: "meta", label: "Meta" },
    { value: "dv360", label: "DV360" },
    { value: "tiktok", label: "TikTok" },
];

const emptyForm = { id: null, name: "", platform: "", scope: "all", campaign_ids: [], threshold_pct: 80, recipients: "", enabled: true };

export default function Alerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [campaigns, setCampaigns] = useState([]);
    const [campSearch, setCampSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/spend-alerts");
            setAlerts(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load alerts");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const loadCampaigns = async (platform) => {
        try {
            const { data } = await api.get(`/spend-alerts/selectable-campaigns${platform ? `?platform=${platform}` : ""}`);
            setCampaigns(data);
        } catch { setCampaigns([]); }
    };

    const openCreate = () => {
        setForm(emptyForm);
        setCampSearch("");
        loadCampaigns("");
        setModalOpen(true);
    };

    const openEdit = (a) => {
        setForm({
            id: a.id,
            name: a.name || "",
            platform: a.platform || "",
            scope: a.campaign_ids && a.campaign_ids.length ? "specific" : "all",
            campaign_ids: a.campaign_ids || [],
            threshold_pct: a.threshold_pct,
            recipients: (a.recipients || []).join(", "),
            enabled: a.enabled,
        });
        setCampSearch("");
        loadCampaigns(a.platform || "");
        setModalOpen(true);
    };

    const onPlatformChange = (p) => {
        setForm((f) => ({ ...f, platform: p, campaign_ids: [] }));
        loadCampaigns(p);
    };

    const toggleCampaign = (id) => {
        setForm((f) => ({
            ...f,
            campaign_ids: f.campaign_ids.includes(id) ? f.campaign_ids.filter((x) => x !== id) : [...f.campaign_ids, id],
        }));
    };

    const save = async () => {
        const recipients = form.recipients.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
        if (!recipients.length) { toast.error("Add at least one recipient email"); return; }
        if (!form.threshold_pct || form.threshold_pct <= 0 || form.threshold_pct > 100) { toast.error("Threshold must be 1–100%"); return; }
        if (form.scope === "specific" && form.campaign_ids.length === 0) { toast.error("Select at least one campaign, or choose All"); return; }
        const payload = {
            name: form.name || "Spend alert",
            platform: form.platform || null,
            campaign_ids: form.scope === "specific" ? form.campaign_ids : null,
            threshold_pct: Number(form.threshold_pct),
            recipients,
            enabled: form.enabled,
        };
        setSaving(true);
        try {
            if (form.id) await api.put(`/spend-alerts/${form.id}`, payload);
            else await api.post("/spend-alerts", payload);
            toast.success(form.id ? "Alert updated" : "Alert created");
            setModalOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save alert");
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (a) => {
        try {
            await api.patch(`/spend-alerts/${a.id}/toggle`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const remove = async (a) => {
        if (!window.confirm("Delete this alert?")) return;
        try {
            await api.delete(`/spend-alerts/${a.id}`);
            toast.success("Alert deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const filteredCampaigns = campaigns.filter((c) => c.name.toLowerCase().includes(campSearch.toLowerCase()));

    return (
        <div className="max-w-5xl" data-testid="alerts-page">
            <div className="flex items-start justify-between mb-1">
                <div>
                    <h1 className="font-display text-2xl text-zinc-900 flex items-center gap-2"><BellRing className="h-6 w-6 wl-accent-text" /> Alerts</h1>
                    <p className="text-zinc-500 text-sm mt-1">Get notified when a campaign's actual spend reaches a share of its total budget.</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 rounded bg-zinc-900 text-white text-sm hover:bg-zinc-800" data-testid="new-alert-btn">
                    <Plus className="h-4 w-4" /> New Alert
                </button>
            </div>

            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 my-4">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Threshold of <strong>50%</strong> sends a single alert. A threshold <strong>above 50%</strong> re-sends every <strong>5 minutes</strong> until you disable the alert here.</span>
            </div>

            {loading ? (
                <div className="text-zinc-400 text-sm py-10 text-center">Loading…</div>
            ) : alerts.length === 0 ? (
                <div className="text-zinc-400 text-sm py-16 text-center border border-dashed border-zinc-200 rounded-lg" data-testid="alerts-empty">No alerts yet. Create one to start monitoring spend.</div>
            ) : (
                <div className="space-y-3">
                    {alerts.map((a) => (
                        <div key={a.id} className="border border-zinc-200 rounded-lg p-4 flex items-center justify-between bg-white" data-testid={`alert-row-${a.id}`}>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-zinc-900 truncate">{a.name}</span>
                                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full border ${a.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>{a.enabled ? "Active" : "Disabled"}</span>
                                </div>
                                <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                    <span>Type: <strong className="text-zinc-700">Spend</strong></span>
                                    <span>Platform: <strong className="text-zinc-700">{a.platform ? a.platform.toUpperCase() : "All"}</strong></span>
                                    <span>Scope: <strong className="text-zinc-700">{a.campaign_ids && a.campaign_ids.length ? `${a.campaign_ids.length} campaign(s)` : "All campaigns"}</strong></span>
                                    <span>Threshold: <strong className="text-zinc-700">{a.threshold_pct}%</strong></span>
                                    <span>Recipients: <strong className="text-zinc-700">{(a.recipients || []).length}</strong></span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                <button onClick={() => toggle(a)} className="px-2.5 py-1.5 rounded text-xs border border-zinc-300 hover:bg-zinc-100" data-testid={`toggle-alert-${a.id}`}>{a.enabled ? "Disable" : "Enable"}</button>
                                <button onClick={() => openEdit(a)} className="p-2 rounded border border-zinc-300 hover:bg-zinc-100" data-testid={`edit-alert-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => remove(a)} className="p-2 rounded border border-zinc-300 hover:bg-red-50 text-red-600" data-testid={`delete-alert-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setModalOpen(false)} data-testid="alert-modal-overlay">
                    <div className="w-full max-w-lg bg-white rounded-lg shadow-xl border border-zinc-200 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="alert-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 sticky top-0 bg-white">
                            <h3 className="font-display text-lg text-zinc-900">{form.id ? "Edit Alert" : "New Alert"}</h3>
                            <button onClick={() => !saving && setModalOpen(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="alert-modal-close"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <label className="block">
                                <span className="text-sm text-zinc-700">Name</span>
                                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Q3 budget watch" className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="alert-name-input" />
                            </label>

                            <label className="block">
                                <span className="text-sm text-zinc-700">Alert type</span>
                                <select disabled className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded text-sm bg-zinc-50" data-testid="alert-type-select"><option>Spend</option></select>
                            </label>

                            <label className="block">
                                <span className="text-sm text-zinc-700">Platform</span>
                                <select value={form.platform} onChange={(e) => onPlatformChange(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="alert-platform-select">
                                    {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </label>

                            <div>
                                <span className="text-sm text-zinc-700">Campaigns</span>
                                <div className="mt-1 flex gap-4 text-sm">
                                    <label className="flex items-center gap-1.5"><input type="radio" checked={form.scope === "all"} onChange={() => setForm({ ...form, scope: "all" })} data-testid="scope-all" /> All campaigns</label>
                                    <label className="flex items-center gap-1.5"><input type="radio" checked={form.scope === "specific"} onChange={() => setForm({ ...form, scope: "specific" })} data-testid="scope-specific" /> Specific</label>
                                </div>
                                {form.scope === "specific" && (
                                    <div className="mt-2 border border-zinc-200 rounded">
                                        <input value={campSearch} onChange={(e) => setCampSearch(e.target.value)} placeholder="Search campaigns…" className="w-full px-3 py-2 border-b border-zinc-200 text-sm" data-testid="campaign-search" />
                                        <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                                            {filteredCampaigns.length === 0 ? (
                                                <div className="text-xs text-zinc-400 py-4 text-center">No campaigns</div>
                                            ) : filteredCampaigns.map((c) => (
                                                <label key={c.id} className="flex items-center gap-2 text-sm px-1.5 py-1 hover:bg-zinc-50 rounded cursor-pointer">
                                                    <input type="checkbox" checked={form.campaign_ids.includes(c.id)} onChange={() => toggleCampaign(c.id)} data-testid={`camp-${c.id}`} />
                                                    <span className="truncate">{c.name}</span>
                                                    <span className="ml-auto text-[10px] text-zinc-400 uppercase">{c.platform}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="text-xs text-zinc-500 px-3 py-1.5 border-t border-zinc-100">{form.campaign_ids.length} selected</div>
                                    </div>
                                )}
                            </div>

                            <label className="block">
                                <span className="text-sm text-zinc-700">Threshold (% of total budget)</span>
                                <input type="number" min="1" max="100" value={form.threshold_pct} onChange={(e) => setForm({ ...form, threshold_pct: e.target.value })} className="mt-1 block w-32 px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="alert-threshold-input" />
                                <span className="block mt-1 text-xs text-zinc-400">50% = one alert. Above 50% = repeats every 5 min until disabled.</span>
                            </label>

                            <label className="block">
                                <span className="text-sm text-zinc-700">Recipients</span>
                                <textarea value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} rows={2} placeholder="email1@company.com, email2@company.com" className="mt-1 block w-full px-3 py-2 border border-zinc-300 rounded text-sm" data-testid="alert-recipients-input" />
                                <span className="block mt-1 text-xs text-zinc-400">Comma or newline separated email addresses.</span>
                            </label>

                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="alert-enabled-check" /> Enable this alert
                            </label>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-100 sticky bottom-0 bg-white">
                            <button onClick={() => setModalOpen(false)} disabled={saving} className="px-4 py-2 rounded text-sm border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50" data-testid="alert-cancel">Cancel</button>
                            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50" data-testid="alert-save">
                                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
